import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import './CheckOutPage.css';
 
interface Price {
  id: string;
  active: boolean;
  billingScheme: string;
  currency: string;
  tiersMode: string | null;
  unitAmount: number;
  recurring: {
    interval: string;
    intervalCount: number;
  };
  tiers?: {
    unitAmount: number;
    upTo: number | null;
  }[];
}

interface Product {
  id: string;
  name: string;
  active: boolean;
  attributes: {
    [key: string]: string;
  };
  features: string[];
  prices: Price[];
}

export const CheckOutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedPriceId, setSelectedPriceId] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [submitting, setSubmitting] = useState(false);

  const productId = searchParams.get('productId');
  const minimumQuantity = (product && product.attributes.minimumQuantity) ? parseInt(product.attributes.minimumQuantity) : 1;
  const trialDays = (product && product.attributes.trialDays) ? parseInt(product.attributes.trialDays) : 0;

  useEffect(() => {
    if (productId) {
      fetchProduct(productId);
    } else {
      setError('No product specified');
      setLoading(false);
    }
  }, [productId]);

  const fetchProduct = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const productData = await api.getProductById(id);
      setProduct(productData);
      
      // Auto-select the first active price
      const activePrice = productData.prices.find((p: Price) => p.active);
      if (activePrice) {
        setSelectedPriceId(activePrice.id);
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Failed to load product details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string = 'usd'): string => {
    //const dollars = amount / 100;
    const dollars = amount;
    if (currency.toLowerCase() === 'usd') {
      return `$${dollars.toFixed(2)}`;
    }
    return `${dollars.toFixed(2)} ${currency.toUpperCase()}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPriceId) {
      setError('Please select a pricing option');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      // Create checkout session
      const response = await api.createCheckoutSession({
        productId: product!.id,
        priceId: selectedPriceId,
        quantity: (quantity < minimumQuantity) ? minimumQuantity : quantity,
        trialDays: trialDays,
        userId: (user) ? user.id : "",
        customerId: (user && user.customers.length > 0) ? user.customers[0].id : "",
      });
      
      // Redirect to checkout URL or Stripe checkout
      console.log(response);
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process checkout');
      setSubmitting(false);
    }
  };

  const hasInstances = user?.customers?.some(c => c.instances && c.instances.length > 0) || false;

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="checkout-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/pricing')} className="btn btn-primary">
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="checkout-page">
        <div className="error-container">
          <h2>Product Not Found</h2>
          <p>The requested product could not be found.</p>
          <button onClick={() => navigate('/pricing')} className="btn btn-primary">
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  /**
   * Example: Create a new customer and assign to instance
   */
  /* This functionality was removed for now
  const handleCreateAndAssignCustomer = async (customerData: NewCustomerData) => {
    // Create the customer
    const customer = await api.createCustomer(customerData);

    // Then assign to instance
    // await api.updateInstance(instanceId, { customerId: customer.id });

    setSelectedCustomerId(customer.id);

    if (user) {
      user.customers.push({
              id: customer.id,
              name: customer.name,
              address: customer.address,
              instances: []});
    }
  };
  */

  return (
    <div className="checkout-page">
      <div className="page-header">
        <h1>Checkout</h1>
        <p>Review your selection and complete your subscription</p>
      </div>

      <div className="checkout-container">
        <div className="checkout-card">
          {/* Product Summary */}
          <div className="product-summary">
            <h2>{product.name}</h2>
            <ul className="features-list">
              {product.features.map((feature, index) => (
                  <li key={index} className="summary-value">{feature}</li>
              ))}
            </ul>
          </div>

          {/* Checkout Form */}
          <form onSubmit={handleSubmit} className="checkout-form">
            {error && (
              <div className="alert-error">
                {error}
              </div>
            )}

            {/* Pricing Options */}
            <div className="pricing-section">
              <h3>Select Your Billing Plan</h3>

              <div className="quantity-selector">
                <label htmlFor="quantity">Number of Subscriptions:</label>
                <select id="quantity" value={(quantity < minimumQuantity) ? minimumQuantity : quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value))}
                    className="quantity-select">
                  {[...Array(20)].map((_, i) => (
                    <option key={i + minimumQuantity} value={i + minimumQuantity}>
                      {i + minimumQuantity}
                    </option>
                  ))}
                </select>
              </div>

              {product.prices.map((price) => {
                if (!price.active) return null;

                const isTiered = price.billingScheme === 'tiered';
                const showTiered = isTiered && !hasInstances;

                if (showTiered && price.tiers) {
                  // Tiered pricing (for new customers)
                  const tier1Amount = formatPrice(price.tiers[0].unitAmount, price.currency);
                  const tier2Amount = price.tiers[1] 
                    ? formatPrice(price.tiers[1].unitAmount, price.currency) 
                    : tier1Amount;

                  return (
                    <div key={price.id} className="pricing-option">
                      <div className="price-radio">
                        <input
                          type="radio"
                          id={price.id}
                          name="price"
                          value={price.id}
                          checked={selectedPriceId === price.id}
                          onChange={(e) => setSelectedPriceId(e.target.value)}
                        />
                        <label htmlFor={price.id}>
                          <span className="price-amount">{tier1Amount}</span>
                          <span className="price-interval">
                            {' '}{price.recurring.interval}ly for one instance
                          </span>
                          {tier2Amount !== tier1Amount && (
                            <span className="tier-note">
                              {' '}• {tier2Amount} for additional instances
                            </span>
                          )}
                        </label>
                      </div>
                      
                      {selectedPriceId === price.id && (
                        <div className="tier-info">
                          <p className="info-text">
                            If you, as a customer, already have one or more instance subscriptions,
                            the graduated price applies. The amount shown will be added to your 
                            existing number of subscriptions.
                          </p>
                          
                          <div className="quantity-selector">
                            <label htmlFor="quantity">Number of Subscriptions:</label>
                            <select
                              id="quantity"
                              value={quantity}
                              onChange={(e) => setQuantity(parseInt(e.target.value))}
                              className="quantity-select"
                            >
                              {[...Array(10)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>
                                  {i + 1}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                } else if (!isTiered) {
                  // Standard per-unit pricing
                  const amount = formatPrice(price.unitAmount, price.currency);
                  
                  return (
                    <div key={price.id} className="pricing-option">
                      <div className="price-radio">
                        <input
                          type="radio"
                          id={price.id}
                          name="price"
                          value={price.id}
                          checked={selectedPriceId === price.id}
                          onChange={(e) => setSelectedPriceId(e.target.value)}
                        />
                        <label htmlFor={price.id}>
                          <span className="price-amount">{amount}</span>
                          <span className="price-interval">
                            {' '}{price.recurring.interval}ly
                          </span>
                        </label>
                      </div>
                    </div>
                  );
                }
                
                return null;
              })}
            </div>

            {/* Actions */}
            <div className="checkout-actions">
              <a 
                href="https://qbdconnect.com/pricing/" 
                className="link-secondary"
              >
                Choose a different subscription
              </a>
              
              <button
                type="submit"
                disabled={submitting || !selectedPriceId}
                className="btn btn-primary btn-large"
              >
                {submitting ? 'Processing...' : 'Subscribe to QBD Connect'}
              </button>
            </div>
          </form>
        </div>

        {/* Order Summary Sidebar */}
        <div className="order-summary">
          <h3>Order Summary</h3>
          
          <div className="summary-item">
            <span className="summary-label">Customer:</span>
            <span className="summary-value">{user?.customers?.[0]?.name ?? 'No customer assigned'}</span>
          </div>

          <div className="summary-item">
            <span className="summary-label">Product:</span>
            <span className="summary-value">{product.name}</span>
          </div>

          {selectedPriceId && (
            <>
              {(() => {
                const selectedPrice = product.prices.find(p => p.id === selectedPriceId);
                if (!selectedPrice) return null;

                let amount = selectedPrice.unitAmount;
                if (selectedPrice.billingScheme === 'tiered' && selectedPrice.tiers) {
                  // Calculate tiered amount
                  if (quantity === 1) {
                    amount = selectedPrice.tiers[0].unitAmount;
                  } else if (selectedPrice.tiers[1]) {
                    amount = selectedPrice.tiers[0].unitAmount + 
                            (selectedPrice.tiers[1].unitAmount * (quantity - 1));
                  }
                } else {
                  amount = selectedPrice.unitAmount * ((quantity < minimumQuantity) ? minimumQuantity : quantity);
                }

                return (
                  <>
                    <div className="summary-item">
                      <span className="summary-label">Billing:</span>
                      <span className="summary-value">
                        {selectedPrice.recurring.interval}ly
                      </span>
                    </div>

                      <div className="summary-item">
                        <span className="summary-label">Quantity:</span>
                        <span className="summary-value">{(quantity < minimumQuantity) ? minimumQuantity : quantity}</span>
                      </div>

                    <div className="summary-divider"></div>

                    <div className="summary-total">
                      <span className="total-label">Total:</span>
                      <span className="total-amount">
                        {formatPrice(amount, selectedPrice.currency)}
                        <span className="total-period">
                          /{selectedPrice.recurring.interval}
                        </span>
                      </span>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
